import type { SessionResponse } from './types'

export class ApiError extends Error {
  readonly code: string
  readonly field: string | undefined
  readonly status: number

  constructor(status: number, body: { code: string; message: string; field?: string }) {
    super(body.message)
    this.name = 'ApiError'
    this.status = status
    this.code = body.code
    this.field = body.field
  }
}

export interface ApiFetchOptions {
  method?: string
  body?: BodyInit | null
  headers?: HeadersInit
  signal?: AbortSignal
}

type ErrorBody = { code: string; message: string; field?: string }

/* The auth error codes the SPA acts on (MS-997; ARCHITECTURE_DECISIONS.md §57):
   - `auth.session_expired` (401): the 60-minute session ran out — renewed
     silently through POST /auth/refresh, then the request is retried once (G5).
   - `auth.reauth_required` (401): the daily sign-in is due, or an Admin reset
     the password — the sign-in page with the "sign in again" notice.
   - `auth.portal_access_denied` (403): the grant was revoked mid-session —
     Access denied, not "session expired" (D10).
   - `auth.unauthorized` (401): no usable session at all — "session expired".
   Each is announced as a window event the AuthProvider listens for, so a
   page never has to handle them itself. */
export const SESSION_EXPIRED_CODE = 'auth.session_expired'
export const REAUTH_REQUIRED_CODE = 'auth.reauth_required'
export const PORTAL_ACCESS_DENIED_CODE = 'auth.portal_access_denied'
export const PORTAL_ADMIN_REQUIRED_CODE = 'auth.portal_admin_required'

/** The 403 refusals `verify_id_token` raises (src/billing_portal/auth/cognito.py),
 * each mapped to Access denied's localised copy of the backend message's
 * English (AccessDenied.tsx). accessDeniedRefusals.test.ts keeps this table
 * and en.json in step with that file. */
export const REFUSAL_COPY: Readonly<
  Record<string, 'accessDenied.emailUnverified' | 'accessDenied.emailMissing' | 'accessDenied.federatedIdentity'>
> = {
  'auth.email_unverified': 'accessDenied.emailUnverified',
  'auth.email_missing': 'accessDenied.emailMissing',
  'auth.federated_identity': 'accessDenied.federatedIdentity',
}
const UNAUTHORIZED_CODE = 'auth.unauthorized'

export const AUTH_EVENTS = {
  sessionExpired: 'auth:session-expired',
  reauthRequired: 'auth:reauth-required',
  accessDenied: 'auth:access-denied',
  sessionRefreshed: 'auth:session-refreshed',
} as const

export const REFRESH_PATH = '/api/v1/auth/refresh'

const REQUEST_TIMEOUT_MS = 30_000

function announce(name: string, detail?: unknown) {
  globalThis.dispatchEvent(new CustomEvent(name, { detail }))
}

async function readErrorBody(response: Response): Promise<ErrorBody> {
  try {
    return (await response.json()) as ErrorBody
  } catch {
    return { code: 'network.error', message: `HTTP ${response.status}` }
  }
}

export type RefreshOutcome = { ok: true; session: SessionResponse } | { ok: false; code: string }

let refreshInFlight: Promise<RefreshOutcome> | null = null

async function postRefresh(): Promise<RefreshOutcome> {
  try {
    const response = await fetch(REFRESH_PATH, {
      method: 'POST',
      credentials: 'include',
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    })
    if (response.ok) return { ok: true, session: (await response.json()) as SessionResponse }
    return { ok: false, code: (await readErrorBody(response)).code }
  } catch {
    return { ok: false, code: 'network.error' }
  }
}

/** POST /auth/refresh, at most once at a time per tab (G5): every request
 * that finds its session expired while a renewal is under way waits for that
 * one instead of starting its own. A renewed session is announced (the
 * AuthProvider takes the fresh profile, the role included). */
export function renewSession(): Promise<RefreshOutcome> {
  refreshInFlight ??= postRefresh()
    .then((outcome) => {
      if (outcome.ok) announce(AUTH_EVENTS.sessionRefreshed, outcome.session)
      return outcome
    })
    .finally(() => {
      refreshInFlight = null
    })
  return refreshInFlight
}

/** What a failed renewal means: a revoked grant is Access denied (D10);
 * anything else — the daily sign-in due, a revoked refresh token, Cognito
 * unreachable (fail closed, Q39) — is "sign in again". */
export function announceRefreshFailure(code: string) {
  if (code === PORTAL_ACCESS_DENIED_CODE) announce(AUTH_EVENTS.accessDenied, { code })
  else announce(AUTH_EVENTS.reauthRequired, { code })
}

function announceError(status: number, body: ErrorBody) {
  if (status === 401 && body.code === UNAUTHORIZED_CODE) announce(AUTH_EVENTS.sessionExpired)
  // An expired session that is still expired after a renewal is treated as
  // "sign in again" rather than retried forever.
  else if (status === 401 && (body.code === REAUTH_REQUIRED_CODE || body.code === SESSION_EXPIRED_CODE))
    announce(AUTH_EVENTS.reauthRequired, { code: body.code })
  else if (status === 403 && body.code === PORTAL_ACCESS_DENIED_CODE)
    announce(AUTH_EVENTS.accessDenied, { code: body.code })
}

function send(path: string, options: ApiFetchOptions | undefined): Promise<Response> {
  const timeout = AbortSignal.timeout(REQUEST_TIMEOUT_MS)
  const signal = options?.signal == null ? timeout : AbortSignal.any([options.signal, timeout])

  const headers = new Headers(options?.headers)
  if (!headers.has('Content-Type') && options?.body != null) {
    headers.set('Content-Type', 'application/json')
  }
  headers.set('X-Request-Id', crypto.randomUUID())

  return fetch(path, {
    method: options?.method,
    body: options?.body,
    credentials: 'include',
    headers,
    signal,
  })
}

/** The body as JSON, or undefined when there is none — a 204, or any 2xx
 * that sends no body. */
async function parse<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T
  }
  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

export async function apiFetch<T>(path: string, options?: ApiFetchOptions): Promise<T> {
  let response = await send(path, options)
  if (response.ok) return parse<T>(response)

  let body = await readErrorBody(response)
  // G5: an expired 60-minute session is renewed (single flight) and the
  // request retried once, with a fresh request id.
  if (response.status === 401 && body.code === SESSION_EXPIRED_CODE) {
    const outcome = await renewSession()
    if (!outcome.ok) {
      announceRefreshFailure(outcome.code)
      throw new ApiError(response.status, body)
    }
    response = await send(path, options)
    if (response.ok) return parse<T>(response)
    body = await readErrorBody(response)
  }
  announceError(response.status, body)
  throw new ApiError(response.status, body)
}

/** Test-only: forget a renewal still in flight between tests. */
export function resetRenewalForTests(): void {
  refreshInFlight = null
}
